import { useQuery } from '@apollo/client';
import useComponentItemActions from 'FeatureModules/ComponentChain/hooks/useComponentItemActions';
import useDialog from 'hooks/useDialog';
import { PLOTS_PER_PAGE, PREVIEW_LATEST_ITEMS } from 'utils/pagination.utils';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { ILogItem } from 'DesignSystem/DataDisplay/LogItem/LogItem';
import { GET_PARTNER_OVERVIEW } from 'graphql/secure/queries/partner.queries';
import { GET_PLOTS_WITH_FARMS } from 'graphql/secure/queries/plot.queries';
import { IPlotsFilters } from 'graphql/secure/queries/types/plot-query.types';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IActivity } from 'FeatureModules/Activity/types/activity.types';
import { ECompanyStatus, ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import { IComponentItem } from 'FeatureModules/ComponentChain/types/component.types';
import { IBasePartnerCompany, IPartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { IPlot } from 'FeatureModules/Site/types/plot.types';
import { TRouteParamsWithId } from 'types/router.types';
import { ESiteType, IBaseSite } from 'FeatureModules/Site/types/site.types';
import { IGraphQlConnection } from 'types/core.types';
import { removeGraphConnections } from 'utils/graphql.utils';

const usePartnerOverview = () => {
  const { id } = useParams<TRouteParamsWithId>();
  const { openDialog } = useDialog();
  const { plan } = useCompanyPlanContext();
  const [skipInviteStepCard, setSkipInviteStepCard] = useState(false);
  const { deleteComponent } = useComponentItemActions();

  const { data, loading, error } = useQuery<
    {
      partner: IPartnerCompany;
    },
    {
      id: string;
      numberOfItems: number;
    }
  >(GET_PARTNER_OVERVIEW, {
    variables: {
      // If id is undefined, the query will be skipped
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: id!,
      numberOfItems: PREVIEW_LATEST_ITEMS,
    },
    skip: !id,
    fetchPolicy: 'network-only',
  });

  const {
    data: plotsData,
    loading: loadingPlots,
    error: plotError,
    fetchMore,
  } = useQuery<{ plots: IGraphQlConnection<IPlot> }, { filters: IPlotsFilters; first: number }>(
    GET_PLOTS_WITH_FARMS,
    {
      variables: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        filters: { ownedByIds: [id!] }, // Id can never be undefined here
        first: PLOTS_PER_PAGE,
      },
      skip: !id,
      notifyOnNetworkStatusChange: true,
    }
  );

  const plots: IPlot[] = useMemo(
    () => (plotsData?.plots ? removeGraphConnections(plotsData.plots) : []),
    [plotsData]
  );

  const hasNextPlotPage: boolean = !!plotsData?.plots?.pageInfo?.hasNextPage;
  const plotsCount: number = plotsData?.plots?.count || 0;

  const handleFetchMorePlots = () => {
    fetchMore({
      variables: {
        after: plotsData?.plots?.pageInfo?.endCursor,
      },
    });
  };

  const isFreemium = plan === ECustomerPlan.FREEMIUM;

  const partner:
    | (IPartnerCompany & {
        componentsCount?: number;
        sharedDocumentsCount?: number;
        requestsCount?: number;
        cultivatedAreaCount?: number;
      })
    | undefined = useMemo(
    () =>
      data?.partner
        ? {
            ...data.partner,
            componentsCount: data.partner.components.count,
            sharedDocumentsCount: data.partner.sharedDocuments.count,
            requestsCount: data.partner.requests?.count,
            plotsCount: plotsCount,
            sitesCount: data.partner.sites.count,
            activitiesCount: data.partner.activities.count,
          }
        : undefined,
    [data, plotsCount]
  );

  const latestLog: Omit<ILogItem, 'title'> = useMemo(
    () => ({
      date: partner?.latestLog?.timestamp || new Date(),
      logo: partner?.latestLog?.company.logo?.url,
      author: `${partner?.latestLog?.user?.firstName ?? ''} ${
        partner?.latestLog?.user?.lastName ?? ''
      }`.trim(),
    }),
    [partner]
  );

  const isAllowedToSendRequests = partner?.status === ECompanyStatus.REGISTERED && !isFreemium;

  const impactClaims = partner?.impactClaims ? removeGraphConnections(partner.impactClaims) : [];
  const requests = partner?.requests ? removeGraphConnections(partner.requests) : [];
  const documents = partner?.sharedDocuments ? removeGraphConnections(partner.sharedDocuments) : [];
  const sites = partner?.sites ? removeGraphConnections(partner.sites) : [];
  const activities = partner?.activities ? removeGraphConnections(partner.activities) : [];
  const components = partner?.components ? removeGraphConnections(partner.components) : [];
  const showInvitePartner: boolean = partner?.status === ECompanyStatus.NOT_INVITED && !isFreemium; // Currently only possible for PAYING customer

  const showReinvitePartnerNextStep: boolean =
    partner?.status === ECompanyStatus.INVITED && !isFreemium; // Currently only possible for PAYING customer

  const hasAnyData =
    impactClaims.length > 0 ||
    requests.length > 0 ||
    documents.length > 0 ||
    sites.length > 0 ||
    activities.length > 0 ||
    components.length > 0 ||
    plotsCount > 0;

  // This is only shown if there is no data yet
  const showInvitePartnerNextStep: boolean =
    showInvitePartner && skipInviteStepCard === false && !hasAnyData;

  const handleAddActivity = () => {
    openDialog({
      type: 'ADD_EDIT_ACTIVITY',
      props: { mode: 'add-activity', partner, hidePartnerSelector: true },
    });
  };

  const handleEditActivity = (activity: IActivity) => {
    openDialog({
      type: 'ADD_EDIT_ACTIVITY',
      props: {
        mode: 'edit-activity',
        activity: {
          ...activity,
        },
      },
    });
  };

  /**
   * Function call coming from site table. Needs to find the base site, verify that it is a farm and load the plots in the dialog
   */
  const handleEditSitePlots = async (siteId: string) => {
    const site: IBaseSite | undefined = sites.find(site => site.id === siteId);
    // In case the site of the cultivation area is not yet loaded, we fetch it
    if (!site) {
      console.error('Site not found');
      return;
    }
    if (site?.siteType === ESiteType.FARM) {
      openDialog({
        type: 'ADD_EDIT_FARM_PLOTS',
        props: {
          farm: site,
        },
      });
    } else {
      console.error('Invalid site type');
      return;
    }
  };

  const handleResendInvite = () => {
    if (!partner) return;
    openDialog({
      type: 'PARTNER_RESEND_INVITE',
      props: {
        companyId: partner.id,
      },
    });
  };

  const handleEditPartner = (enableInvite = false) => {
    openDialog({
      type: 'ADD_EDIT_PARTNER',
      props: { partner, enableInvite },
    });
  };

  const handleAddComponent = () => {
    openDialog({
      type: 'ADD_EDIT_COMPONENT',
      props: { title: 'Add new component', partner },
    });
  };

  const handleEditComponent = (component: IComponentItem) => {
    openDialog({
      type: 'ADD_EDIT_COMPONENT',
      props: { title: 'Edit component', componentItem: component, partner },
    });
  };

  const handleDeleteComponent = (component: IComponentItem) => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete component',
        displayCloseButton: true,
        itemTitle: component.title,
        text: 'Are you sure you want to delete this component? All data will be lost and you will not be able to recover this item.',
        submitText: 'Delete',
        onSubmit: () => {
          deleteComponent({ variables: { id: component.id } });
        },
        onCancel: () => undefined,
      },
    });
  };

  return {
    partner: partner as IBasePartnerCompany & {
      componentsCount?: number;
      sharedDocumentsCount?: number;
      requestsCount?: number;
      plotsCount?: number;
      sitesCount?: number;
      activitiesCount?: number;
    },
    loading,
    loadingPlots,
    error,
    plotError,
    hasAnyData,
    requests,
    documents,
    sites,
    activities,
    components,
    plots,
    impactClaims,
    latestLog,
    isAllowedToSendRequests,
    isFreemium,
    showInvitePartner,
    showInvitePartnerNextStep,
    showReinvitePartnerNextStep,
    hasNextPlotPage,
    handleSkipInviteStep: setSkipInviteStepCard,
    handleAddActivity,
    handleEditActivity,
    handleEditSitePlots,
    handleResendInvite,
    handleEditPartner,
    handleAddComponent,
    handleEditComponent,
    handleDeleteComponent,
    handleFetchMorePlots,
  };
};

export default usePartnerOverview;
